<template>
    <div v-if="actions.length > 0" data-testid="weekly-actions-list" class="journeys-action-list flex flex-col">
        <section v-for="group in activeActionGroups" :key="group.key" class="journeys-action-group flex flex-col" :aria-labelledby="`weekly-action-group-${group.key}`">
            <h3 :id="`weekly-action-group-${group.key}`" class="journeys-action-group__label flex items-center gap-1">
                <span class="size-2 rounded-full bg-[#81DADA]" aria-hidden="true"></span>
                {{ group.title }}
            </h3>
            <ul class="flex flex-col gap-2.5">
                <li v-for="action in group.actions" :key="action.id">
                    <WeeklyActionCard
                        :action="action"
                        :role-play-setup-url="rolePlaySetupUrl"
                        :is-current-week="isCurrentWeek"
                        :timezone="timezone"
                        :is-updating="updatingActionIds.includes(action.id)"
                        :is-conversation-launching="isConversationLaunching"
                        @update-status="(actionId, status) => emit('update-status', actionId, status)"
                        @view-details="(actionId) => emit('view-details', actionId)"
                        @start-conversation="(initialUserMessage) => emit('start-conversation', initialUserMessage)"
                        @discuss-action="(actionId) => emit('discuss-action', actionId)" />
                </li>
            </ul>
        </section>

        <section v-if="completedActionGroups.length > 0" class="journeys-completed-section flex flex-col" aria-labelledby="completed-actions-title">
            <h3 id="completed-actions-title" class="journeys-completed-section__title">{{ completedSectionTitle }}</h3>
            <div v-for="group in completedActionGroups" :key="group.key" class="flex flex-col gap-2.5">
                <h4 class="journeys-completed-section__group flex items-center gap-1">
                    <span class="size-2 rounded-full bg-[#81DADA]" aria-hidden="true"></span>
                    {{ group.title }}
                </h4>
                <ul class="flex flex-col gap-2.5">
                    <li v-for="action in group.actions" :key="action.id">
                        <WeeklyActionCard
                            :action="action"
                            :role-play-setup-url="rolePlaySetupUrl"
                            :is-current-week="isCurrentWeek"
                            :timezone="timezone"
                            :is-updating="updatingActionIds.includes(action.id)"
                            :is-conversation-launching="isConversationLaunching"
                            @update-status="(actionId, status) => emit('update-status', actionId, status)"
                            @view-details="(actionId) => emit('view-details', actionId)"
                            @start-conversation="(initialUserMessage) => emit('start-conversation', initialUserMessage)"
                            @discuss-action="(actionId) => emit('discuss-action', actionId)" />
                    </li>
                </ul>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import WeeklyActionCard from "./WeeklyActionCard.vue";
import type { WeeklyAction, WeeklyActionStatus } from "./weeklyActions";

const props = defineProps<{
    actions: WeeklyAction[];
    rolePlaySetupUrl: string;
    isCurrentWeek: boolean;
    timezone?: string;
    completedSectionTitle: string;
    updatingActionIds: string[];
    isConversationLaunching?: boolean;
}>();

const emit = defineEmits<{
    "update-status": [actionId: string, status: WeeklyActionStatus];
    "view-details": [actionId: string];
    "start-conversation": [initialUserMessage: string];
    "discuss-action": [actionId: string];
}>();

type ActionGroup = { key: string; title: string; actions: WeeklyAction[] };

const activeActionGroups = computed<ActionGroup[]>(() => groupActions(props.actions.filter((action) => action.status !== "completed")));
const completedActionGroups = computed<ActionGroup[]>(() => groupActions(props.actions.filter((action) => action.status === "completed")));

function groupActions(actions: WeeklyAction[]): ActionGroup[] {
    const groups = new Map<string, ActionGroup>();
    for (const action of actions) {
        const goal = action.development_goal;
        const key = goal?.id ?? "independent-actions";
        const title = goal?.title ?? "Actions";
        const group = groups.get(key) ?? { key, title, actions: [] };
        group.actions.push(action);
        groups.set(key, group);
    }
    return [...groups.values()];
}
</script>

<style scoped>
.journeys-action-list {
    gap: 20px;
    padding: 20px;
}

.journeys-action-group {
    gap: 3px;
}

.journeys-action-group + .journeys-action-group {
    margin-top: 20px;
}

.journeys-action-group__label,
.journeys-completed-section__title,
.journeys-completed-section__group {
    color: #6e6860;
    font-family: var(--font-scaffold, "Space Grotesk", sans-serif);
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
}

.journeys-completed-section {
    gap: 10px;
    padding-top: 20px;
    border-top: 1px solid #e7e2db;
}
</style>
